// sectionsConfig.js
import React from 'react'

// components
import { Avatar, Box, Chip, CircularProgress, Divider, RadioGroup, Typography } from '@mui/material'
import { Controller } from 'react-hook-form'
import AdxInput from 'components/custom/AdxInput'
import TextField from '@mui/material/TextField'
import AdxSwitch from 'components/reusable/switch/AdxSwitch'
import AdxRadioButton from 'components/custom/AdxRadioButton'
import AdxFormControlLabel from 'components/custom/AdxFormControlLabel'
import AdxDatePickerV2 from 'components/custom/AdxDatePickerV2'
import AdxDropdown from 'components/custom/AdxDropdown'
import AdxMenuItem from 'components/custom/AdxMenuItem'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import defaultAvatar from 'assets/images/default_avatar.png'
import AdxAutocomplete from 'components/reusable/AdxAutocomplete'
import AdxButton from 'components/custom/AdxButton'
import AttendanceLabelRenderer from 'components/custom/AdxLabelRenderer'

// services
import { calculateTimeDifferenceFormatted, formatToDDMMYYYY } from 'services/helper/DateTime'
import dropdownOptions from './dropdownOptions'
import { parse, differenceInMinutes, isSameDay } from 'date-fns'

// icons
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import ClearIcon from '@mui/icons-material/Clear'
import AddIcon from '@mui/icons-material/Add'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import InfoIcon from '@mui/icons-material/Info'

// location restriction import
import MapImage from 'module/time-attendance/components/MapImage'
import SearchIcon from '@mui/icons-material/Search'
import GpsFixedIcon from '@mui/icons-material/GpsFixed'

export const getSections = ({
  register,
  control,
  setSelectedStartDate,
  selectedStartDate,
  setSelectedEndDate,
  selectedEndDate,
  noExpiryDate,
  setNoExpiryDate,
  lateSwitch,
  setLateSwitch,
  departments,
  selectedDepartments,
  setSelectedDepartments,
  employees,
  addedEmployees,
  setAddedEmployees,
  groupType,
  setGroupType,
  addedDepartments,
  setAddedDepartments,
  workingHours,
  setWorkingHours,
  workingMode,
  setWorkingMode,
  isSameHours,
  setIsSameHours,
  hours,
  setHours,
  shiftData,
  errors,
  editable,
  combinedAssignments,
  isInactiveAndPastStart,
  selectedRadius,
  setSelectedRadius,
  customRadiusValue,
  setCustomRadiusValue,
  customRadiusUnit,
  setCustomRadiusUnit,
  outsideZoneAction,
  setOutsideZoneAction,
  onUseCurrentLocation,
  locationData,
  permission,
  coordinates,
  locationLoading,
  geocodingLoading,
  geocodingError,
  restrictByLocation,
  setRestrictByLocation,
  selectedLocation,
  setSelectedLocation,
  locationSearch,
  setLocationSearch,
  hasLocationSearchSupport,
  locationSearchResults,
  locationSearchLoading,
  onLocationSearchSelect,
}) => {
  // -----------------------------
  // Helpers
  // -----------------------------
  const createData = (statusType, definition, icon, status) => ({ statusType, definition, icon, status })

  const dateToday = new Date()
  const isDevelopment = process.env.NODE_ENV === 'development'

  const safeArray = (v) => (Array.isArray(v) ? v : [])
  const rawSelectedLatitude = selectedLocation?.latitude
  const rawSelectedLongitude = selectedLocation?.longitude
  const selectedLatitude =
    rawSelectedLatitude === '' || rawSelectedLatitude == null ? null : Number(rawSelectedLatitude)
  const selectedLongitude =
    rawSelectedLongitude === '' || rawSelectedLongitude == null
      ? null
      : Number(rawSelectedLongitude)
  const hasSelectedCoordinates =
    selectedLatitude != null &&
    selectedLongitude != null &&
    Number.isFinite(selectedLatitude) &&
    Number.isFinite(selectedLongitude)
  const gpsAccuracyColor =
    coordinates?.accuracy <= 20
      ? '#00E4AA'
      : coordinates?.accuracy <= 50
        ? '#F3BF3A'
        : coordinates?.accuracy <= 100
          ? '#FF9500'
          : '#FF6B6B'

  // -----------------------------
  // ✅ Shift assignment helpers (keep UI/UX same)
  // - Backend flag: option.has_shift means "assigned to some shift"
  // - If assigned to THIS shift, we must NOT treat it as "other shift"
  // -----------------------------
  const assignedThisShiftIndividualIds = new Set(safeArray(shiftData?.shift_assignment?.individuals))
  const assignedThisShiftDepartmentIds = new Set(safeArray(shiftData?.shift_assignment?.department))

  const selectedEmployeeIds = new Set(safeArray(addedEmployees).map((x) => x?.id).filter(Boolean))
  const selectedDepartmentIds = new Set(safeArray(selectedDepartments).map((x) => x?.id).filter(Boolean))

  const getOptionMeta = (option, type) => {
    const id = option?.id
    if (!id) return { isSelected: false, isAssignedToThisShift: false, isAssignedToOtherShift: false }

    if (type === 'employee') {
      const isAssignedToThisShift = assignedThisShiftIndividualIds.has(id)
      const isAssignedToOtherShift = Boolean(option?.has_shift) && !isAssignedToThisShift
      const isSelected = selectedEmployeeIds.has(id)
      return { isSelected, isAssignedToThisShift, isAssignedToOtherShift }
    }

    // department
    const isAssignedToThisShift = assignedThisShiftDepartmentIds.has(id)
    const isAssignedToOtherShift = Boolean(option?.has_shift) && !isAssignedToThisShift
    const isSelected = selectedDepartmentIds.has(id)
    return { isSelected, isAssignedToThisShift, isAssignedToOtherShift }
  }

  // Selected → top, then assigned-to-this-shift, then normal, then assigned-to-other (disabled) last
  const sortRosterOptions = (list, type) => {
    const arr = [...safeArray(list)]
    arr.sort((a, b) => {
      const ma = getOptionMeta(a, type)
      const mb = getOptionMeta(b, type)

      const rank = (m) => {
        if (m.isSelected) return 0
        if (m.isAssignedToThisShift) return 1
        if (m.isAssignedToOtherShift) return 3
        return 2
      }

      const ra = rank(ma)
      const rb = rank(mb)
      if (ra !== rb) return ra - rb

      const an = String(a?.name ?? '').toLowerCase()
      const bn = String(b?.name ?? '').toLowerCase()
      return an.localeCompare(bn)
    })
    return arr
  }

  const isAssignedToThisShift = (option, type) => {
    if (!option?.id) return false
    return type === 'employee'
      ? assignedThisShiftIndividualIds.has(option.id)
      : assignedThisShiftDepartmentIds.has(option.id)
  }

  const isAssignedToOtherShift = (option, type) => {
    if (!option?.has_shift) return false
    return !isAssignedToThisShift(option, type)
  }

  // Select all (exclude assigned-to-other shift, keep assigned-to-this-shift allowed)
  const handleSelectAllIndividuals = () => {
    const allIndividuals = safeArray(employees).filter((emp) => {
      const assignedThis = assignedThisShiftIndividualIds.has(emp.id)
      const assignedOther = Boolean(emp?.has_shift) && !assignedThis
      return !assignedOther
    })
    setAddedEmployees(allIndividuals)
  }

  const handleSelectAllDepartments = () => {
    const allDepts = safeArray(departments).filter((dept) => {
      const assignedThis = assignedThisShiftDepartmentIds.has(dept.id)
      const assignedOther = Boolean(dept?.has_shift) && !assignedThis
      return !assignedOther
    })
    setSelectedDepartments(allDepts)
  }

  const handleAddDepartments = () => {
    // preserve your existing behaviour: dropdown selection -> chips list
    setAddedDepartments(selectedDepartments)
  }

  const handleDelete = (item, type) => {
    if (type === 'department') {
      const temp = safeArray(selectedDepartments).filter((d) => d !== item)
      setSelectedDepartments(temp)
      setAddedDepartments(temp)
      return
    }
    if (type === 'employee') {
      const temp = safeArray(addedEmployees).filter((e) => e !== item)
      setAddedEmployees(temp)
    }
  }

  // -----------------------------
  // Working hours helpers
  // -----------------------------
  const calculateTotalHours = (start, end) => {
    const startTime = parse(start, 'HH:mm', new Date())
    const endTime = parse(end, 'HH:mm', new Date())

    let totalMinutes = differenceInMinutes(endTime, startTime)
    if (totalMinutes < 0) totalMinutes += 24 * 60

    const h = Math.floor(totalMinutes / 60)
    const m = totalMinutes % 60
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
  }

  const handleSameHoursChange = (field, value) => {
    const updatedHours = { ...hours, [field]: value }

    if (workingMode === 1 && updatedHours.start_time && updatedHours.end_time) {
      updatedHours.total_hours = calculateTotalHours(updatedHours.start_time, updatedHours.end_time)
    }

    setHours(updatedHours)
  }

  const handleRowChange = (field, value, dayIndex) => {
    const updated = [...safeArray(workingHours)]
    if (dayIndex === -1) return

    updated[dayIndex][field] = value

    if (workingMode === 1) {
      const start = updated[dayIndex].start_time
      const end = updated[dayIndex].end_time
      if (start && end) {
        updated[dayIndex].total_hours = calculateTotalHours(start, end)
      }
    }

    // Flexible mode total_hours changes are already stored in field
    setWorkingHours(updated)
  }

  // -----------------------------
  // Attendance ruling rows
  // -----------------------------
  const rows = [
    createData(
      <Typography>Present</Typography>,
      <Box>
        <Typography>Completed the total hours duration</Typography>
      </Box>,
      <AttendanceLabelRenderer status={'Present'} />,
      <Typography>Enabled</Typography>
    ),
    createData(
      <Typography>Incomplete</Typography>,
      <Box>
        <Typography>Did not complete the total hours duration</Typography>
      </Box>,
      <AttendanceLabelRenderer status={'Incomplete'} />,
      <Typography>Enabled</Typography>
    ),
    createData(
      <Typography>Absent</Typography>,
      <Box>
        <Typography>Did not clock in at all</Typography>
      </Box>,
      <AttendanceLabelRenderer status={'Absent'} />,
      <Typography>Enabled</Typography>
    ),
    createData(
      <Typography>Late</Typography>,
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        {editable ? (
          <>
            <Typography sx={{ fontWeight: 'bold' }}>Select one only</Typography>

            {/* NOTE:
              - keep your UI/UX
              - "lateType" is only for UI switching; onSubmit already sends late_duration/late_last_clockin
            */}
            <Controller
              name="lateType"
              control={control}
              defaultValue={workingMode === 2 ? 1 : 2}
              render={({ field }) => (
                <RadioGroup
                  value={field.value}
                  onChange={(e) => field.onChange(Number(e.target.value))}
                >
                  <AdxFormControlLabel
                    checked={workingMode === 2}
                    disabled={workingMode !== 2}
                    sx={{ alignItems: 'start' }}
                    label={'Assign last clock in time (flexible hours only)'}
                    description={
                      <Box sx={{ display: 'flex', marginTop: '8px', gap: '8px' }}>
                        <Typography sx={{ color: 'light-gray-text.main' }}>Set time:</Typography>
                        <Controller
                          name="late_last_clockin"
                          control={control}
                          render={({ field: f }) => (
                            <AdxDropdown
                              value={f.value}
                              onChange={(e) => f.onChange(e)}
                              disabled={workingMode !== 2}
                              sxDropdown={{ width: '255px' }}
                            >
                              {dropdownOptions.time.map((item, index) => (
                                <AdxMenuItem key={index} value={item.value}>
                                  <Typography variant="body1">{item.label}</Typography>
                                </AdxMenuItem>
                              ))}
                            </AdxDropdown>
                          )}
                        />
                      </Box>
                    }
                    value={1}
                    type="no-fill"
                    control={<AdxRadioButton />}
                  />

                  <AdxFormControlLabel
                    checked={workingMode === 1}
                    disabled={workingMode !== 1}
                    sx={{ alignItems: 'start' }}
                    label={'Assign clock in window from start time (fixed hours only)'}
                    description={
                      <Box sx={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                        <Typography sx={{ color: 'light-gray-text.main' }}>Duration:</Typography>
                        <Controller
                          name="late_duration"
                          control={control}
                          render={({ field: f }) => (
                            <AdxDropdown
                              value={f.value}
                              onChange={(e) => f.onChange(e)}
                              disabled={workingMode !== 1}
                              sxDropdown={{ width: '255px' }}
                            >
                              {dropdownOptions.clock_in_window.map((item, index) => (
                                <AdxMenuItem key={index} value={item.value}>
                                  <Typography variant="body1">{item.label}</Typography>
                                </AdxMenuItem>
                              ))}
                            </AdxDropdown>
                          )}
                        />
                      </Box>
                    }
                    value={2}
                    type="no-fill"
                    control={<AdxRadioButton />}
                  />
                </RadioGroup>
              )}
            />

            <Box
              sx={{
                display: 'flex',
                gap: '12px',
                padding: '12px',
                backgroundColor: 'gray-background.main',
                borderRadius: '4.5px',
              }}
            >
              <Box>
                <InfoIcon sx={{ color: 'information.main', fontSize: '20px' }} />
              </Box>
              <Typography>
                Any clock in time that exceeded the selected time will be considered late
              </Typography>
            </Box>
          </>
        ) : (
          <>
            <Typography>Late rule</Typography>
            <AdxInput
              value={
                shiftData?.late_duration
                  ? `${shiftData?.late_duration} hrs`
                  : shiftData?.late_last_clockin
                    ? `${shiftData?.late_last_clockin}`
                    : '--'
              }
              disabled
            />
          </>
        )}
      </Box>,
      <AttendanceLabelRenderer status={'Late'} />,
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px' }}>
        {editable ? (
          <Controller
            name="late"
            control={control}
            defaultValue={lateSwitch}
            render={({ field }) => (
              <AdxSwitch
                label1={'Enabled'}
                label2={'Disabled'}
                state={field.value}
                onChange={() => {
                  setLateSwitch(!lateSwitch)
                  field.onChange(!lateSwitch)
                }}
              />
            )}
          />
        ) : (
          <Typography>{lateSwitch ? 'Enabled' : 'Disabled'}</Typography>
        )}
      </Box>
    ),
    createData(
      <Typography>Underperform</Typography>,
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        <Typography>Both late and did not complete the total hours duration</Typography>
        <Box
          sx={{
            display: 'flex',
            gap: '12px',
            padding: '12px',
            backgroundColor: 'gray-background.main',
            borderRadius: '4.5px',
          }}
        >
          <Box>
            <InfoIcon sx={{ color: 'information.main', fontSize: '20px' }} />
          </Box>
          <Typography>Automatically available once the late ruling is enabled</Typography>
        </Box>
      </Box>,
      <AttendanceLabelRenderer status={'Underperform'} />,
      <Typography>{lateSwitch ? 'Enabled' : 'Disabled'}</Typography>
    ),
  ]

  // -----------------------------
  // Sections
  // -----------------------------
  return [
    // -----------------------------
    // General
    // -----------------------------
    {
      title: 'General',
      content: (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label>Shift name</label>
            <AdxInput
              disabled={!editable}
              placeholder="E.g Normal Shift"
              inputProps={{ maxLength: 50 }}
              errorMessage={
                <Typography sx={{ color: 'error.main' }}>{errors?.name?.message}</Typography>
              }
              isErrorShown={Boolean(errors?.name?.message)}
              {...register('name')}
            />
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label>Description</label>
            <TextField
              inputProps={{ maxLength: 300 }}
              disabled={!editable}
              multiline
              placeholder="Describe the shift for admin reference"
              rows={4}
              sx={{
                width: '100%',
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { border: 0, borderColor: 'light-gray-text.main' },
                  '&:hover fieldset': { border: 0, borderColor: 'light-gray-text.main' },
                  '&.Mui-focused fieldset': { border: 0, borderColor: 'alternate.main' },
                },
              }}
              {...register('description')}
            />
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label>Shift period</label>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography sx={{ color: 'light-gray-text.main' }}>
                Set a time period for this shift.
              </Typography>

              {editable && (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <AdxSwitch state={noExpiryDate} onChange={() => setNoExpiryDate(!noExpiryDate)} />
                  <Typography>No expiry date</Typography>
                </Box>
              )}
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center', width: '100%' }}>
                <Typography
                  sx={{
                    color: isInactiveAndPastStart
                      ? 'light-gray-text-2.main'
                      : 'dark-text.main',
                  }}
                  noWrap
                >
                  Start date:
                </Typography>
                <AdxDatePickerV2
                  selectedDate={selectedStartDate}
                  setSelectedDate={setSelectedStartDate}
                  sx={{
                    width: '300px',
                    backgroundColor: editable === false && 'gray-background.main',
                  }}
                  iconStart="left"
                  disabled={editable === false || isInactiveAndPastStart}
                  disablePast={!isDevelopment}
                  shouldDisableDate={(date) => !isDevelopment && isSameDay(date, dateToday)}
                />
              </Box>

              {(editable || shiftData?.end_date) && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '8px',
                    alignItems: 'center',
                    width: '100%',
                  }}
                >
                  <Typography
                    sx={{
                      color: noExpiryDate ? 'light-gray-text-2.main' : 'dark-text.main',
                    }}
                    noWrap
                  >
                    End date:
                  </Typography>
                  <AdxDatePickerV2
                    selectedDate={selectedEndDate}
                    setSelectedDate={setSelectedEndDate}
                    disabled={noExpiryDate === true || editable === false}
                    sx={{ width: '300px', backgroundColor: !editable && 'gray-background.main' }}
                    iconStart="left"
                    disablePast={!isDevelopment}
                    shouldDisableDate={(date) => !isDevelopment && isSameDay(date, dateToday)}
                  />
                </Box>
              )}
            </Box>
          </Box>

          {noExpiryDate === false && (
            <Box
              sx={{
                padding: '12px 14px',
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                backgroundColor: 'default-background.main',
                color: 'light-gray-text.main',
                borderRadius: '4.5px',
              }}
            >
              <CalendarMonthIcon />
              <Typography>{`Shift will expire on ${formatToDDMMYYYY(
                shiftData?.end_date ? new Date(shiftData?.end_date) : selectedEndDate
              )}`}</Typography>
            </Box>
          )}
        </Box>
      ),
    },

    // -----------------------------
    // Working hours (UI unchanged)
    // -----------------------------
    {
      title: 'Working hours',
      content: (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {editable && (
            <>
              <label>Select working mode</label>
              <Controller
                name="working_mode_status_id"
                control={control}
                defaultValue={workingMode}
                render={({ field }) => (
                  <RadioGroup
                    value={field.value}
                    onChange={(e) => {
                      const numValue = Number(e.target.value)
                      field.onChange(numValue)
                      setWorkingMode(numValue)
                    }}
                  >
                    <AdxFormControlLabel
                      label={'Fixed hours'}
                      value={1}
                      description={'Set a specific time for clock in and end time for clock out.'}
                      type="no-fill"
                      control={<AdxRadioButton />}
                    />
                    <AdxFormControlLabel
                      label={'Flexible hours'}
                      value={2}
                      description={'Set total hours required for employees to complete for each day.'}
                      type="no-fill"
                      control={<AdxRadioButton />}
                    />
                  </RadioGroup>
                )}
              />
            </>
          )}

          {/* FIXED HOURS (editable) */}
          {workingMode === 1 && editable && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', paddingY: '12px' }}>
                <Typography>Define working time</Typography>
              </Box>
              <TableContainer
                sx={{
                  boxShadow: 'none',
                  border: 1,
                  borderColor: 'gray-line.main',
                  borderRadius: '4.5px',
                }}
              >
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'default-background.main' }}>
                      <TableCell sx={{ width: '20%' }}>
                        <Typography
                          variant="subtitle2"
                          sx={{ color: 'light-gray-text.main', textAlign: 'left' }}
                        >
                          Day
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ width: '20%' }}>
                        <Typography
                          variant="subtitle2"
                          sx={{ color: 'light-gray-text.main', textAlign: 'left' }}
                        >
                          Start time
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ width: '20%' }}>
                        <Typography
                          variant="subtitle2"
                          sx={{ color: 'light-gray-text.main', textAlign: 'left' }}
                        >
                          End time
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ width: '20%' }}>
                        <Typography
                          variant="subtitle2"
                          sx={{ color: 'light-gray-text.main', textAlign: 'left' }}
                        >
                          Total hours
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ width: '20%' }}>
                        <Typography
                          variant="subtitle2"
                          sx={{ color: 'light-gray-text.main', textAlign: 'right' }}
                        >
                          Working day
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    <TableRow>
                      <TableCell>
                        <AdxDropdown value={isSameHours} onChange={(e) => setIsSameHours(e.target.value)}>
                          <AdxMenuItem value={true}>
                            <Typography variant="body1">Same hours</Typography>
                          </AdxMenuItem>
                          <AdxMenuItem value={false}>
                            <Typography variant="body1">Custom hours</Typography>
                          </AdxMenuItem>
                        </AdxDropdown>
                      </TableCell>

                      <TableCell sx={{ display: 'flex', alignItems: 'center', gap: '30px' }}>
                        <AdxDropdown
                          sx={{ flexGrow: 1 }}
                          value={!isSameHours ? '--:--' : hours.start_time}
                          disabled={!isSameHours}
                          onChange={(e) => handleSameHoursChange('start_time', e.target.value)}
                        >
                          <AdxMenuItem disabled value={'--:--'}>
                            <Typography variant="body1">--:--</Typography>
                          </AdxMenuItem>
                          {dropdownOptions.time.map((option, index) => (
                            <AdxMenuItem key={index} value={option.value}>
                              <Typography variant="body1">{option.label}</Typography>
                            </AdxMenuItem>
                          ))}
                        </AdxDropdown>
                        <Typography>to</Typography>
                      </TableCell>

                      <TableCell>
                        <AdxDropdown
                          value={!isSameHours ? '--:--' : hours.end_time}
                          disabled={!isSameHours}
                          onChange={(e) => handleSameHoursChange('end_time', e.target.value)}
                        >
                          <AdxMenuItem disabled value={'--:--'}>
                            <Typography variant="body1">--:--</Typography>
                          </AdxMenuItem>
                          {dropdownOptions.time.map((option, index) => (
                            <AdxMenuItem key={index} value={option.value}>
                              <Typography variant="body1">{option.label}</Typography>
                            </AdxMenuItem>
                          ))}
                        </AdxDropdown>
                      </TableCell>

                      <TableCell />
                      <TableCell />
                    </TableRow>

                    {safeArray(workingHours).map((day, index) => (
                      <TableRow key={index} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                        <TableCell>
                          <Typography sx={{ textTransform: 'capitalize' }}>{day.day}</Typography>
                        </TableCell>

                        <TableCell sx={{ display: 'flex', alignItems: 'center', gap: '30px' }}>
                          <AdxDropdown
                            sx={{ flexGrow: 1 }}
                            value={isSameHours ? hours.start_time : day.start_time}
                            onChange={(e) => handleRowChange('start_time', e.target.value, index)}
                            disabled={!day.working_day || isSameHours}
                          >
                            {dropdownOptions.time.map((option, idx) => (
                              <AdxMenuItem key={idx} value={option.value}>
                                <Typography variant="body1">{option.label}</Typography>
                              </AdxMenuItem>
                            ))}
                          </AdxDropdown>
                          <Typography>to</Typography>
                        </TableCell>

                        <TableCell>
                          <AdxDropdown
                            value={isSameHours ? hours.end_time : day.end_time}
                            onChange={(e) => handleRowChange('end_time', e.target.value, index)}
                            disabled={!day.working_day || isSameHours}
                          >
                            {dropdownOptions.time.map((option, idx) => (
                              <AdxMenuItem key={idx} value={option.value}>
                                <Typography variant="body1">{option.label}</Typography>
                              </AdxMenuItem>
                            ))}
                          </AdxDropdown>
                        </TableCell>

                        <TableCell>
                          <AdxInput
                            disabled
                            value={`${(isSameHours ? hours.total_hours ?? '--:--' : day.total_hours ?? '--:--')} hours`}
                          />
                        </TableCell>

                        <TableCell align="right">
                          <AdxSwitch
                            state={day.working_day}
                            onChange={(e) => handleRowChange('working_day', e.target.checked, index)}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          {/* FLEXIBLE HOURS (editable) */}
          {workingMode === 2 && editable && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingY: '12px' }}>
                <Typography>Define working time</Typography>
              </Box>

              <TableContainer
                sx={{
                  boxShadow: 'none',
                  border: 1,
                  borderColor: 'gray-line.main',
                  borderRadius: '4.5px',
                }}
              >
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'default-background.main' }}>
                      <TableCell sx={{ width: '20%' }}>
                        <Typography variant="subtitle2" sx={{ color: 'light-gray-text.main', textAlign: 'left' }}>
                          Day
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ width: '60%' }}>
                        <Typography variant="subtitle2" sx={{ color: 'light-gray-text.main', textAlign: 'left' }}>
                          Total hours
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ width: '10%' }}>
                        <Typography variant="subtitle2" sx={{ color: 'light-gray-text.main', textAlign: 'right' }}>
                          Working day
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    <TableRow>
                      <TableCell>
                        <AdxDropdown value={isSameHours} onChange={(e) => setIsSameHours(e.target.value)}>
                          <AdxMenuItem value={true}>
                            <Typography variant="body1">Same hours</Typography>
                          </AdxMenuItem>
                          <AdxMenuItem value={false}>
                            <Typography variant="body1">Custom hours</Typography>
                          </AdxMenuItem>
                        </AdxDropdown>
                      </TableCell>

                      <TableCell>
                        <AdxDropdown
                          sx={{ width: '60%' }}
                          value={!isSameHours ? '--:--' : hours.total_hours}
                          disabled={!isSameHours}
                          onChange={(e) => handleSameHoursChange('total_hours', e.target.value)}
                        >
                          <AdxMenuItem disabled value={'--:--'}>
                            <Typography variant="body1">--:--</Typography>
                          </AdxMenuItem>
                          {dropdownOptions.hour.map((option, index) => (
                            <AdxMenuItem key={index} value={option}>
                              <Typography variant="body1">
                                {option} {option === '01:00' ? 'hour' : 'hours'}
                              </Typography>
                            </AdxMenuItem>
                          ))}
                        </AdxDropdown>
                      </TableCell>

                      <TableCell />
                    </TableRow>

                    {safeArray(workingHours).map((day, index) => (
                      <TableRow key={index} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                        <TableCell>
                          <Typography sx={{ textTransform: 'capitalize' }}>{day.day}</Typography>
                        </TableCell>

                        <TableCell sx={{ display: 'flex', alignItems: 'center', gap: '30px' }}>
                          <AdxDropdown
                            sx={{ width: '60%' }}
                            value={isSameHours ? hours.total_hours : day.total_hours}
                            onChange={(e) => handleRowChange('total_hours', e.target.value, index)}
                            disabled={!day.working_day || isSameHours}
                          >
                            {dropdownOptions.hour.map((option, idx) => (
                              <AdxMenuItem key={idx} value={option}>
                                <Typography variant="body1">
                                  {option} {option === '01:00' ? 'hour' : 'hours'}
                                </Typography>
                              </AdxMenuItem>
                            ))}
                          </AdxDropdown>
                        </TableCell>

                        <TableCell align="right">
                          <Box>
                            <AdxSwitch
                              label1={'Yes'}
                              label2={'No'}
                              state={day.working_day}
                              onChange={(e) => handleRowChange('working_day', e.target.checked, index)}
                            />
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          {/* VIEW MODE tables unchanged (but safe fallbacks) */}
          {workingMode === 1 && !editable && (
            <TableContainer sx={{ boxShadow: 'none', border: 1, borderColor: 'gray-line.main', borderRadius: '4.5px' }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'default-background.main' }}>
                    <TableCell sx={{ width: '20%' }}>
                      <Typography variant="subtitle2" sx={{ color: 'light-gray-text.main', textAlign: 'left' }}>
                        Day
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ width: '20%' }}>
                      <Typography variant="subtitle2" sx={{ color: 'light-gray-text.main', textAlign: 'left' }}>
                        Start time
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ width: '20%' }}>
                      <Typography variant="subtitle2" sx={{ color: 'light-gray-text.main', textAlign: 'left' }}>
                        End time
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ width: '20%' }}>
                      <Typography variant="subtitle2" sx={{ color: 'light-gray-text.main', textAlign: 'left' }}>
                        Total hours
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {safeArray(shiftData?.working_time).map((day, index) => (
                    <TableRow key={index} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                      <TableCell>
                        <Typography sx={{ textTransform: 'capitalize' }}>{day.day}</Typography>
                      </TableCell>
                      <TableCell sx={{ display: 'flex', alignItems: 'center', gap: '30px' }}>
                        <AdxDropdown sx={{ flexGrow: 1 }} value={day.start_time ?? '--:--'} disabled>
                          <AdxMenuItem disabled value={'--:--'}>
                            <Typography variant="body1">--:--</Typography>
                          </AdxMenuItem>
                          {dropdownOptions.time.map((option, idx) => (
                            <AdxMenuItem key={idx} value={option.value}>
                              <Typography variant="body1">{option.label}</Typography>
                            </AdxMenuItem>
                          ))}
                        </AdxDropdown>
                        <Typography>to</Typography>
                      </TableCell>
                      <TableCell>
                        <AdxDropdown value={day.end_time ?? '--:--'} disabled>
                          <AdxMenuItem disabled value={'--:--'}>
                            <Typography variant="body1">--:--</Typography>
                          </AdxMenuItem>
                          {dropdownOptions.time.map((option, idx) => (
                            <AdxMenuItem key={idx} value={option.value}>
                              <Typography variant="body1">{option.label}</Typography>
                            </AdxMenuItem>
                          ))}
                        </AdxDropdown>
                      </TableCell>
                      <TableCell>
                        <AdxInput
                          disabled
                          value={`${day.start_time && day.end_time ? calculateTimeDifferenceFormatted(day.start_time, day.end_time) : '--:--'} hours`}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {workingMode === 2 && !editable && (
            <TableContainer sx={{ boxShadow: 'none', border: 1, borderColor: 'gray-line.main', borderRadius: '4.5px' }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'default-background.main' }}>
                    <TableCell sx={{ width: '20%' }}>
                      <Typography variant="subtitle2" sx={{ color: 'light-gray-text.main', textAlign: 'left' }}>
                        Day
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ width: '20%' }}>
                      <Typography variant="subtitle2" sx={{ color: 'light-gray-text.main', textAlign: 'left' }}>
                        Total hours
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {safeArray(shiftData?.working_time).map((day, index) => (
                    <TableRow key={index} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                      <TableCell>
                        <Typography sx={{ textTransform: 'capitalize' }}>{day.day}</Typography>
                      </TableCell>
                      <TableCell>
                        <AdxInput disabled value={`${day.total_hours ?? '--:--'} hours`} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      ),
    },

    // -----------------------------
    // Duty rostering (keep previous UI/UX)
    // -----------------------------
    {
      title: 'Duty rostering',
      content: (
        <>
          {editable ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px', width: '100%' }}>
              <Typography sx={{ color: 'light-gray-text.main' }}>
                Assign employees scheduled for this shift. Only one shift is allowed for an employee within the same time frame.
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label>Select group type</label>
                  <AdxDropdown
                    sxDropdown={{ width: '255px' }}
                    value={groupType}
                    onChange={(e) => setGroupType(e.target.value)}
                  >
                    <AdxMenuItem value={1} selectedvalue={groupType}>
                      <Typography variant="body1">Departments</Typography>
                    </AdxMenuItem>
                    <AdxMenuItem value={2} selectedvalue={groupType}>
                      <Typography variant="body1">Individual</Typography>
                    </AdxMenuItem>
                  </AdxDropdown>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
                  <label>Select assignee</label>

                  <AdxAutocomplete
                    disabled={groupType === 0}
                    noOptionsText={groupType === 1 ? 'No departments found' : 'No employees found'}
                    disableClearable
                    renderTags={() => null}
                    onChange={(event, value) => {
                      if (groupType === 1) setSelectedDepartments(value)
                      else setAddedEmployees(value)
                    }}
                    value={groupType === 1 ? selectedDepartments : addedEmployees}
                    PaperComponent={({ children }) => {
                      if (groupType === 1) {
                        return (
                          <Paper elevation={2} sx={{ padding: '12px' }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  padding: '12px',
                                }}
                              >
                                <Typography sx={{ fontWeight: 'bold' }}>Select departments</Typography>
                                <Typography
                                  sx={{ color: 'alternate.main', textDecoration: 'underline', cursor: 'pointer' }}
                                  onMouseDown={(event) => {
                                    event.preventDefault()
                                    event.stopPropagation()
                                    handleSelectAllDepartments()
                                  }}
                                >
                                  Select all
                                </Typography>
                              </Box>
                            </Box>

                            {children}

                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'end',
                                padding: '12px 24px 0px 24px ',
                                borderTop: 1,
                                borderTopColor: 'gray-line.main',
                                marginX: '-12px',
                              }}
                            >
                              <AdxButton
                                variant="contained"
                                sx={{ width: '120px' }}
                                onMouseDown={(event) => {
                                  event.stopPropagation()
                                  handleAddDepartments()
                                }}
                              >
                                Add
                              </AdxButton>
                            </Box>
                          </Paper>
                        )
                      }

                      return (
                        <Paper elevation={2} sx={{ padding: '12px' }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '12px',
                              }}
                            >
                              <Typography sx={{ fontWeight: 'bold' }}>Select individuals</Typography>
                              <Typography
                                sx={{ color: 'alternate.main', textDecoration: 'underline', cursor: 'pointer' }}
                                onMouseDown={(event) => {
                                  event.preventDefault()
                                  event.stopPropagation()
                                  handleSelectAllIndividuals()
                                }}
                              >
                                Select all
                              </Typography>
                            </Box>
                          </Box>

                          {children}
                        </Paper>
                      )
                    }}
                    renderOption={(props, option) => {
                      const type = groupType === 1 ? 'department' : 'employee'

                      const selected =
                        type === 'department'
                          ? safeArray(selectedDepartments).some((d) => d.id === option.id)
                          : safeArray(addedEmployees).some((e) => e.id === option.id)

                      const assignedThis = isAssignedToThisShift(option, type)
                      const assignedOther = isAssignedToOtherShift(option, type)

                      const handleOptionClick = (event) => {
                        event.preventDefault()
                        event.stopPropagation()
                        if (assignedOther) return

                        if (type === 'department') {
                          setSelectedDepartments((prev) =>
                            selected ? prev.filter((x) => x.id !== option.id) : [...prev, option]
                          )
                        } else {
                          setAddedEmployees((prev) =>
                            selected ? prev.filter((x) => x.id !== option.id) : [...prev, option]
                          )
                        }
                      }

                      // Departments: keep checkbox UI
                      if (type === 'department') {
                        return (
                          <Box
                            key={option.id}
                            onClick={handleOptionClick}
                            sx={{
                              my: '5px',
                              padding: '12px',
                              borderRadius: '4.5px',
                              display: 'flex',
                              justifyContent: 'space-between',
                              width: '100%',
                              color: assignedOther ? 'light-gray-text.main' : '',
                              cursor: assignedOther ? 'not-allowed' : 'pointer',
                              ':hover': { backgroundColor: 'alternate.light' },
                              backgroundColor: assignedOther ? 'default-background.main' : '',
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                              <Typography>{option.name}</Typography>

                              {assignedOther && (
                                <Typography variant="subtitle1" sx={{ color: 'error.main' }}>
                                  (already assigned to different shift)
                                </Typography>
                              )}

                              {!assignedOther && assignedThis && (
                                <Typography variant="subtitle1" sx={{ color: 'alternate.main' }}>
                                  (already assigned to this shift)
                                </Typography>
                              )}
                            </Box>

                            {selected ? (
                              <CheckBoxIcon sx={{ color: 'alternate.main' }} />
                            ) : (
                              <CheckBoxOutlineBlankIcon sx={{ color: 'light-gray-text.main' }} />
                            )}
                          </Box>
                        )
                      }

                      // Individuals: keep previous UI (no checkbox look)
                      return (
                        <Box
                          key={option.id}
                          onClick={handleOptionClick}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '8px',
                            gap: '12px',
                            borderRadius: '4.5px',
                            width: '100%',
                            color: assignedOther ? 'light-gray-text.main' : '',
                            cursor: assignedOther ? 'not-allowed' : 'pointer',
                            ':hover': { backgroundColor: 'alternate.light' },
                            backgroundColor: assignedOther ? 'default-background.main' : '',
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              borderRadius: '100%',
                              height: '32px',
                              width: '32px',
                            }}
                          >
                            <Avatar
                              alt="profile"
                              sx={{ width: 32, height: 32 }}
                              src={option?.profile_image_url || defaultAvatar}
                            />
                          </Box>

                          <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <Typography>{option.name}</Typography>

                            {assignedOther && (
                              <Typography variant="subtitle1" sx={{ color: 'error.main' }}>
                                (already assigned to different shift)
                              </Typography>
                            )}

                            {!assignedOther && assignedThis && (
                              <Typography variant="subtitle1" sx={{ color: 'alternate.main' }}>
                                (already assigned to this shift)
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      )
                    }}
                    popupIcon={<AddIcon />}
                    multiple
                    options={
                      groupType === 1
                        ? sortRosterOptions(departments || [], 'department')
                        : sortRosterOptions(employees || [], 'employee')
                    }
                    getOptionLabel={(option) => option.name}
                    renderInput={(params) => (
                      <TextField
                        InputLabelProps={{ sx: { display: 'none' } }}
                        placeholder={
                          groupType === 1
                            ? selectedDepartments?.length > 0
                              ? 'Search'
                              : 'Select departments'
                            : addedEmployees?.length > 0
                              ? 'Search'
                              : 'Select employees'
                        }
                        {...params}
                      />
                    )}
                  />
                </Box>
              </Box>

              {safeArray(addedDepartments).length > 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <Typography sx={{ color: 'light-gray-text.main' }}>Assigned departments</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {safeArray(addedDepartments).map((dept, index) => (
                      <Chip
                        key={index}
                        onDelete={() => handleDelete(dept, 'department')}
                        deleteIcon={<ClearIcon />}
                        label={dept.name}
                        sx={{
                          padding: '8px',
                          width: 'auto',
                          height: '45px',
                          borderRadius: '24px',
                          color: 'dark-text.main',
                          backgroundColor: 'default-background.main',
                          '& .MuiChip-deleteIcon': { color: 'error.main' },
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {safeArray(addedEmployees).length > 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <Typography sx={{ color: 'light-gray-text.main' }}>Assigned individuals</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {safeArray(addedEmployees).map((emp, index) => (
                      <Chip
                        key={index}
                        avatar={<Avatar alt="avatar" src={emp.profile_image_url || defaultAvatar} />}
                        onDelete={() => handleDelete(emp, 'employee')}
                        deleteIcon={<ClearIcon />}
                        label={emp.name}
                        sx={{
                          padding: '8px',
                          width: 'auto',
                          height: '45px',
                          borderRadius: '24px',
                          color: 'dark-text.main',
                          backgroundColor: 'default-background.main',
                          '& .MuiChip-deleteIcon': { color: 'error.main' },
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
              <Typography sx={{ color: 'light-gray-text.main' }}>Assigned groups</Typography>

              {safeArray(combinedAssignments).length > 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {safeArray(combinedAssignments).map((assignee, index) => (
                      <Chip
                        key={index}
                        label={assignee?.name}
                        sx={{
                          padding: '8px',
                          width: 'auto',
                          height: '45px',
                          borderRadius: '24px',
                          color: 'dark-text.main',
                          backgroundColor: 'default-background.main',
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </>
      ),
    },

    // -----------------------------
    // Advanced settings (unchanged UI)
    // -----------------------------
    {
      title: 'Advanced settings',
      content: (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <Typography>Attendance ruling</Typography>
            <Typography sx={{ color: 'light-gray-text.main' }}>
              Configure your shift attendance rules and it will be applied to the attendance status.
            </Typography>
          </Box>

          <Box>
            <TableContainer
              sx={{
                border: 1,
                borderRadius: '4.5px',
                borderColor: 'gray-line.main',
                boxShadow: 'none',
              }}
            >
              <Table sx={{ minWidth: 650 }} aria-label="simple table">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: 'light-gray-text.main', borderRight: 1, borderRightColor: 'gray-line.main' }}>
                      <Typography>Status type</Typography>
                    </TableCell>
                    <TableCell sx={{ color: 'light-gray-text.main', borderRight: 1, borderRightColor: 'gray-line.main' }}>
                      <Typography>Rule applied</Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ color: 'light-gray-text.main', borderRight: 1, borderRightColor: 'gray-line.main' }}>
                      <Typography>Icon</Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ color: 'light-gray-text.main' }}>
                      <Typography>Activation</Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {rows.map((row, index) => (
                    <TableRow key={index} sx={{ '&:last-child td, &:last-child th': { borderBottom: 0 } }}>
                      <TableCell component="th" scope="row" sx={{ borderRight: 1, borderRightColor: 'gray-line.main' }}>
                        {row.statusType}
                      </TableCell>
                      <TableCell align="left" sx={{ borderRight: 1, borderRightColor: 'gray-line.main' }}>
                        {row.definition}
                      </TableCell>
                      <TableCell sx={{ borderRight: 1, borderRightColor: 'gray-line.main' }}>
                        {row.icon}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          color:
                            row?.statusType?.props?.children === 'Present' ||
                              row?.statusType?.props?.children === 'Incomplete' ||
                              row?.statusType?.props?.children === 'Absent' ||
                              row?.statusType?.props?.children === 'Underperform' ||
                              !editable
                              ? 'light-gray-text.main'
                              : 'dark-text.main',
                          backgroundColor:
                            row?.statusType?.props?.children === 'Present' ||
                              row?.statusType?.props?.children === 'Incomplete' ||
                              row?.statusType?.props?.children === 'Absent' ||
                              row?.statusType?.props?.children === 'Underperform' ||
                              !editable
                              ? 'default-background.main'
                              : null,
                          width: '250px',
                        }}
                      >
                        {row.status}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
      ),
    },

    // ------------------------------------
    // Location restriction (unchanged UI)
    // ------------------------------------
    {
      title: 'Location restriction',
      content: (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>

          <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <Typography fontWeight={600}>Use default location</Typography>
              <Typography>
                Require Work from office clock-ins to happen within a configured office radius.
              </Typography>
            </Box>

            <AdxSwitch
              state={restrictByLocation}
              onChange={() => editable && setRestrictByLocation(!restrictByLocation)}
            />
          </Box>

          <Divider sx={{ marginY: '16px' }} />

          {restrictByLocation && (
            <Box>

              {/* <Divider sx={{ marginY: '16px' }} /> */}

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%' }}>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', mb: '8px' }}>
                  <Typography fontWeight={600}>Restrict clock-in by location</Typography>
                  <Typography sx={{ color: 'light-gray-text.main' }}>
                    Staff can only clock in or out within a set radius of the designated location.
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{ fontWeight: 'bold' }}>Zone name</label>
                  <AdxInput
                    sxInput={{
                      border: '1px solid #e5e5e5',
                    }}
                    disabled={!editable}
                    placeholder="Lumora HQ - Main Office"
                    inputProps={{ maxLength: 50 }}
                    value={selectedLocation?.name || ''}
                    onChange={setSelectedLocation('name')}
                    errorMessage={
                      <Typography sx={{ color: 'error.main' }}>
                        {errors?.location_name?.message}
                      </Typography>
                    }
                    isErrorShown={Boolean(errors?.location_name?.message)}
                  />
                </Box>

                {/* Location address input section */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{ fontWeight: 'bold' }}>Location address</label>

                  {/* Search + Auto Locate row */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      flexWrap: 'wrap',
                    }}
                  >

                    {/* Search input — has the border */}
                    <Box sx={{ flex: 1, minWidth: '260px' }}>
                      <AdxAutocomplete
                        disablePortal
                        disabled={!editable}
                        options={locationSearchResults || []}
                        loading={locationSearchLoading}
                        inputValue={locationSearch}
                        onInputChange={(_, value, reason) => {
                          if (reason === 'input' || reason === 'clear') {
                            setLocationSearch(value)
                          }
                        }}
                        onChange={onLocationSearchSelect}
                        getOptionLabel={(option) =>
                          option?.display_name || option?.address || option?.name || ''
                        }
                        isOptionEqualToValue={(option, value) => option?.id === value?.id}
                        noOptionsText={
                          locationSearch?.trim()?.length >= 2
                            ? 'No locations found'
                            : 'Type at least 2 characters'
                        }
                        renderOption={(props, option) => (
                          <li {...props} key={option?.id}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                              <Typography sx={{ fontWeight: 600 }}>
                                {option?.name || option?.display_name}
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'light-gray-text.main' }}>
                                {option?.address || option?.display_name}
                              </Typography>
                            </Box>
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Search for office address..."
                            InputProps={{
                              ...params.InputProps,
                              startAdornment: (
                                <>
                                  <SearchIcon
                                    sx={{
                                      color: 'light-gray-text.main',
                                      fontSize: '20px',
                                      mr: 1,
                                    }}
                                  />
                                  {params.InputProps.startAdornment}
                                </>
                              ),
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '4.5px',
                                backgroundColor: 'white.main',
                                '& fieldset': { borderColor: 'gray-line.main' },
                                '&:hover fieldset': { borderColor: 'gray-line.main' },
                                '&.Mui-focused fieldset': { borderColor: 'alternate.main' },
                              },
                            }}
                          />
                        )}
                      />
                    </Box>

                    {/* Auto Locate — outside the border */}
                    <Box
                      component="button"
                      type="button"
                      onClick={onUseCurrentLocation}
                      disabled={!editable || locationLoading}
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        border: 'none',
                        outline: 'none',
                        backgroundColor: 'transparent',
                        color: 'alternate.main',
                        cursor: !editable || locationLoading ? 'default' : 'pointer',
                        padding: 0,
                        minWidth: 'fit-content',
                        '&:disabled': {
                          opacity: 0.6,
                        },
                      }}
                    >
                      {locationLoading ? (
                        <CircularProgress size={16} sx={{ color: 'alternate.main' }} />
                      ) : (
                        <GpsFixedIcon sx={{ fontSize: '18px' }} />
                      )}
                      <Typography
                        sx={{
                          color: 'alternate.main',
                          fontWeight: 500,
                          textDecoration: 'underline',
                        }}
                      >
                        Auto Locate
                      </Typography>
                    </Box>

                  </Box>

                  {/* Address card — only shown once location is detected */}
                  {errors?.location_address?.message && (
                    <Typography sx={{ color: 'error.main' }}>
                      {errors?.location_address?.message}
                    </Typography>
                  )}

                  {!hasLocationSearchSupport && (
                    <Box
                      sx={{
                        display: 'flex',
                        gap: '12px',
                        padding: '12px',
                        backgroundColor: 'gray-background.main',
                        borderRadius: '4.5px',
                      }}
                    >
                      <Box>
                        <InfoIcon sx={{ color: 'information.main', fontSize: '20px' }} />
                      </Box>
                      <Typography>
                        Address search is not wired in this repo yet because there is no forward
                        geocoding or place-search utility available. Use current location or edit the
                        final location fields manually for now.
                      </Typography>
                    </Box>
                  )}

                  {(selectedLocation?.address || locationData || geocodingLoading || geocodingError) && (
                    <Box sx={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      gap: '14px', padding: '14px', backgroundColor: 'default-background.main', borderRadius: '4.5px',
                    }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 'bold', wordBreak: 'break-word' }}>
                          {geocodingLoading
                            ? 'Fetching current location details...'
                            : selectedLocation?.name ||
                            locationData?.building_name ||
                            locationData?.primary_location ||
                            locationData?.formatted_address ||
                            locationData?.display_name ||
                            'Location detected'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: geocodingError ? 'error.main' : 'text.secondary', display: 'block', fontSize: '0.75rem', mt: '4px', wordBreak: 'break-word' }}>
                          {geocodingError
                            ? 'Unable to reverse geocode the detected GPS position. You can still fill the fields manually.'
                            : selectedLocation?.address ||
                            locationData?.formatted_address ||
                            locationData?.display_name}
                        </Typography>
                        {coordinates?.accuracy && (
                          <Typography variant="caption" sx={{
                            fontSize: '0.75rem', fontWeight: 'bold', mt: 0.5, display: 'block',
                            color: gpsAccuracyColor
                          }}>
                            GPS: ±{Math.round(coordinates.accuracy)}m
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  )}

                  {/* Map — MapImage already renders its own "Location Map • Your location / OpenStreetMap" header */}
                  {(errors?.location_latitude?.message || errors?.location_longitude?.message) && (
                    <Typography sx={{ color: 'error.main' }}>
                      Select a valid location from search or use current location.
                    </Typography>
                  )}

                  {hasSelectedCoordinates && (
                    <MapImage
                      latitude={selectedLatitude}
                      longitude={selectedLongitude}
                      width={480}
                      height={320}
                      zoom={15}
                      sx={{ ml: 3 }}
                    />
                  )}
                </Box>

                {/* Radius input section */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <Box>
                    <Typography fontWeight={600}>Allowed radius</Typography>
                    <Typography>
                      Radius enforcement applies during clock-in only, and only for Work from office.
                    </Typography>
                  </Box>

                  {/* Radius preset buttons */}
                  <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {[100, 200, 500, 1000, 'custom'].map((option) => {
                      const isSelected = selectedRadius === option
                      const label =
                        option === 'custom' ? 'Custom' : option === 1000 ? '1 km' : `${option} m`
                      return (
                        <Box
                          key={option}
                          onClick={() => editable && setSelectedRadius(option)}
                          sx={{
                            padding: '8px 20px',
                            borderRadius: '999px',
                            border: '1px solid',
                            borderColor: isSelected ? 'alternate.main' : 'gray-line.main',
                            backgroundColor: isSelected ? 'alternate.main' : 'transparent',
                            color: isSelected ? '#fff' : 'dark-text.main',
                            cursor: editable ? 'pointer' : 'default',
                            userSelect: 'none',
                          }}
                        >
                          <Typography variant="body2" fontWeight={isSelected ? 600 : 400}>
                            {label}
                          </Typography>
                        </Box>
                      )
                    })}
                  </Box>

                  {/* Custom radius input — only shown when Custom is selected */}
                  {selectedRadius === 'custom' && (
                    <Box sx={{ display: 'flex', gap: '8px' }}>
                      <AdxInput
                        disabled={!editable}
                        placeholder="Enter Value"
                        sxInput={{ border: '1px solid #e5e5e5', flexGrow: 1 }}
                        value={customRadiusValue}
                        onChange={(e) => setCustomRadiusValue(e.target.value)}
                        inputProps={{ type: 'number', min: 1, step: 1 }}
                        errorMessage={
                          <Typography sx={{ color: 'error.main' }}>
                            {errors?.allowed_radius_meters?.message}
                          </Typography>
                        }
                        isErrorShown={Boolean(errors?.allowed_radius_meters?.message)}
                      />
                      <AdxDropdown
                        value={customRadiusUnit}
                        onChange={(e) => setCustomRadiusUnit(e.target.value)}
                        disabled={!editable}
                        sxDropdown={{ width: '160px' }}
                      >
                        <AdxMenuItem value="m">
                          <Typography variant="body1">meter (m)</Typography>
                        </AdxMenuItem>
                        <AdxMenuItem value="km">
                          <Typography variant="body1">kilometer (km)</Typography>
                        </AdxMenuItem>
                      </AdxDropdown>
                    </Box>
                  )}

                  {selectedRadius !== 'custom' && errors?.allowed_radius_meters?.message && (
                    <Typography sx={{ color: 'error.main' }}>
                      {errors?.allowed_radius_meters?.message}
                    </Typography>
                  )}
                </Box>

                <Divider sx={{ marginY: '16px' }} />

                {/* Staff outside zone */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <Typography fontWeight={600}>When staff are outside the radius</Typography>

                  <Controller
                    name="out_of_radius_action"
                    control={control}
                    defaultValue={outsideZoneAction}
                    render={({ field }) => (
                      <RadioGroup
                        value={field.value}
                        onChange={(e) => {
                          field.onChange(e.target.value)
                          setOutsideZoneAction(e.target.value)
                        }}
                      >
                        <AdxFormControlLabel
                          label={'Block clock-in'}
                          value="block"
                          description={'Staff cannot clock in until they enter the allowed radius'}
                          type="no-fill"
                          control={<AdxRadioButton />}
                          disabled={!editable}
                        />
                        <AdxFormControlLabel
                          label={'Allow with warning'}
                          value="warn"
                          description={'Clock-in succeeds and the staff sees a warning message'}
                          type="no-fill"
                          control={<AdxRadioButton />}
                          disabled={!editable}
                        />
                      </RadioGroup>
                    )}
                  />

                  {errors?.out_of_radius_action?.message && (
                    <Typography sx={{ color: 'error.main' }}>
                      {errors?.out_of_radius_action?.message}
                    </Typography>
                  )}
                </Box>

              </Box>


            </Box>
          )}
        </Box>
      ),
    }
  ]
}
